/**
 * Iter 67ej — Vendor Referrals dashboard panel.
 *
 * Surfaces the vendor's own referral code with a shareable signup link,
 * lists everyone they've referred with status (pending → qualified →
 * rewarded) and a copy-link button.
 *
 * Reward semantics (current): refer a fellow rental company; once they
 * list ≥3 active cars, you earn 1 month commission-free. Admin redeems
 * via the platform-wide ledger.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Gift, Copy, Check, Share2, RefreshCw, Loader2,
  CircleCheckBig, CircleDashed, Trophy, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const _STATUS_PILL = {
  pending:   { Icon: CircleDashed,    cls: "bg-amber-50 text-amber-700 border-amber-200",     label: "Pending"   },
  qualified: { Icon: CircleCheckBig,  cls: "bg-blue-50 text-blue-700 border-blue-200",         label: "Qualified" },
  rewarded:  { Icon: Trophy,          cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Rewarded"  },
  void:      { Icon: AlertCircle,     cls: "bg-neutral-100 text-neutral-600 border-neutral-200", label: "Void"     },
};

export default function VendorReferralsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/vendor/referrals");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const code = data?.referral_code || "";
  const shareUrl = code
    ? `${window.location.origin}/register?role=vendor&ref=${encodeURIComponent(code)}`
    : "";

  // Robust copy with execCommand fallback — same pattern as the vendor
  // credentials sheet to avoid the React error overlay on insecure /
  // sandboxed contexts.
  const copyText = async (text, setFlag) => {
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch { /* fall through */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        ta.remove();
      } catch { ok = false; }
    }
    if (ok) {
      setFlag(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setFlag(false), 1500);
    } else {
      toast.error("Couldn't copy — please copy the value manually.");
    }
  };

  const share = async () => {
    if (!shareUrl) return;
    if (navigator?.share) {
      try {
        await navigator.share({
          title: "Join RentalCar.al as a rental partner",
          text: `Use my code ${code} when signing up.`,
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled or unsupported — fall through */ }
    }
    copyText(shareUrl, setCopiedLink);
  };

  return (
    <div className="space-y-5" data-testid="vendor-referrals-panel">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
            <Gift className="h-4 w-4 text-amber-600" />
            Refer a fellow rental company
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Share your code with another vendor. Once they list at least <strong>{data?.reward_threshold_cars ?? 3}</strong> active cars,
            you earn <strong>1 month commission-free</strong> on your platform fee.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-full"
          data-testid="vendor-referrals-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </header>

      {/* Code + share card */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-amber-50/30 to-orange-50 p-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Your referral code</div>
            {loading ? (
              <div className="h-9 w-40 mt-2 rounded-md bg-amber-100/60 animate-pulse" />
            ) : code ? (
              <div className="font-mono text-3xl font-bold tracking-wider mt-1 text-amber-900 select-all" data-testid="vendor-referral-code">{code}</div>
            ) : (
              <div className="text-sm text-amber-800 mt-2 italic">No code yet — refresh to generate one.</div>
            )}
            {shareUrl && (
              <div className="mt-3 text-[11px] text-amber-900/80 truncate select-all font-mono" data-testid="vendor-referral-link">
                {shareUrl}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0 justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copyText(code, setCopiedCode)}
              disabled={!code}
              className="rounded-full bg-white"
              data-testid="vendor-referral-copy-code"
            >
              {copiedCode ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
              {copiedCode ? "Copied" : "Copy code"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copyText(shareUrl, setCopiedLink)}
              disabled={!shareUrl}
              className="rounded-full bg-white"
              data-testid="vendor-referral-copy-link"
            >
              {copiedLink ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
              {copiedLink ? "Copied" : "Copy link"}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={share}
              disabled={!shareUrl}
              className="rounded-full bg-amber-600 hover:bg-amber-700"
              data-testid="vendor-referral-share"
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Share
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="vendor-referrals-stats">
        <_Stat label="Total"     value={data?.stats?.total ?? 0}     icon={Gift}           tone="amber"   />
        <_Stat label="Pending"   value={data?.stats?.pending ?? 0}   icon={CircleDashed}   tone="neutral" />
        <_Stat label="Qualified" value={data?.stats?.qualified ?? 0} icon={CircleCheckBig} tone="blue"    />
        <_Stat label="Rewarded"  value={data?.stats?.rewarded ?? 0}  icon={Trophy}         tone="emerald" />
      </div>

      {/* Ledger table */}
      <div className="rounded-2xl border border-border overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <tr>
                <th className="px-3 py-2 text-left">Vendor</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Cars</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Signed up</th>
                <th className="px-3 py-2 text-left">Qualified</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="6" className="px-3 py-6 text-center text-neutral-400">Loading…</td></tr>
              )}
              {!loading && (data?.items?.length ?? 0) === 0 && (
                <tr><td colSpan="6" className="px-3 py-6 text-center text-neutral-500">
                  No referrals yet. Share your code above with a peer rental company to get started.
                </td></tr>
              )}
              {(data?.items || []).map((r) => {
                const pill = _STATUS_PILL[r.status] || _STATUS_PILL.pending;
                const StatusIcon = pill.Icon;
                return (
                  <tr key={r.id} className="border-t border-border" data-testid={`vendor-referral-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-neutral-900">
                      <div>{r.referee_business_name || r.referee_name || "—"}</div>
                      {r.referee_business_name && r.referee_name && r.referee_business_name !== r.referee_name && (
                        <div className="text-[10px] text-neutral-500">{r.referee_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-neutral-700 truncate max-w-[200px]" title={r.referee_email}>
                      {r.referee_email || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className="text-[10px]">
                        {r.qualified_car_count ?? 0}/{data?.reward_threshold_cars ?? 3}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${pill.cls}`}>
                        <StatusIcon className="h-3 w-3" />
                        {pill.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-neutral-500">
                      {r.qualified_at ? new Date(r.qualified_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-neutral-400 leading-relaxed">
        Rewards are applied by an admin within a few business days of qualifying.
        We may void referrals where the referee turns out to be a duplicate account
        or where we detect referral fraud.
      </p>
    </div>
  );
}

function _Stat({ label, value, icon: Icon, tone }) {
  const tones = {
    amber:   "bg-amber-50 text-amber-700 ring-amber-200",
    blue:    "bg-blue-50 text-blue-700 ring-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    neutral: "bg-neutral-50 text-neutral-700 ring-neutral-200",
  };
  return (
    <div className={`rounded-xl ring-1 px-3 py-2 ${tones[tone] || tones.neutral}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-display font-medium leading-tight mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
